import { Shield, Mail, Terminal, Code, ArrowLeft } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import {
  Form,
  Link,
  useActionData,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import {
  VerifyCodeFormActionResponse,
  VerifyCodeFormData,
} from "./types/verifyCodeTypes";
import { useTranslation } from "react-i18next";

const VerifyCode = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigation = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const actionData = useActionData() as VerifyCodeFormActionResponse;
  const email =
    (location.state as { email: string })?.email ||
    actionData?.verifyCodeData?.email;
  const isSubmitting = navigation.state === "submitting";
  useEffect(() => {
    if (!actionData) return;
    if (actionData.shouldRedirect && navigation.state === "idle") {
      navigate("/reset-password", {
        state: {
          email: actionData.verifyCodeData.email,
          code: actionData.verifyCodeData.code,
        } satisfies VerifyCodeFormData,
      });
    }
  }, [actionData, navigation.state, navigate]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Paste handling
      const pastedCode = value.slice(0, 6).split("");
      const newCode = [...code];
      pastedCode.forEach((char, i) => {
        if (index + i < 6) {
          newCode[index + i] = char;
        }
      });
      setCode(newCode);

      // Focus on the last filled input or next empty one
      const nextIndex = Math.min(index + pastedCode.length, 5);
      inputRefs.current[nextIndex]?.focus();
    } else {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-6xl bg-white rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px] lg:min-h-[600px]">
          {/* Verify Code Form Section */}
          <div className="flex flex-col justify-center p-4 sm:p-6 lg:p-12">
            <div className="w-full max-w-md mx-auto">
              {/* Form Header */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary rounded-lg p-2">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold">
                  {t("verifyCode.title")}
                </h1>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                  {t("verifyCode.subtitle")}
                </p>
                <div className="flex items-center justify-center mt-3 text-sm text-gray-500">
                  <Mail className="h-4 w-4 mr-1" />
                  <span>{email}</span>
                </div>
              </div>

              {/* Code Input Form */}
              <Form method="post" action="/verify-code" className="space-y-6">
                <input type="hidden" name="email" value={email} />
                <input type="hidden" name="code" value={code.join("")} />
                {/* Code Input Fields */}
                <div className="flex justify-center space-x-2 sm:space-x-3">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-12 sm:w-14 sm:h-14 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      disabled={isSubmitting}
                    />
                  ))}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || code.join("").length !== 6}
                  className={`w-full h-11 text-base font-medium text-white rounded-md transition-colors duration-300 ${
                    isSubmitting || code.join("").length !== 6
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-primary-600 cursor-pointer"
                  }`}
                >
                  {isSubmitting
                    ? t("verifyCode.verifying")
                    : t("verifyCode.verifyButton")}
                </button>

                {/* Back to Login */}
                <Link
                  to="/forgot-password"
                  className="flex items-center justify-center text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  <ArrowLeft className="inline h-5 w-5 mr-1 align-text-bottom" />
                  <p>{t("verifyCode.useDifferentEmail")}</p>
                </Link>
              </Form>
            </div>
          </div>

          {/* Security Illustration Section */}
          <div className="hidden lg:flex bg-gradient-to-br from-primary-50 to-primary-100 items-center justify-center p-8 lg:p-12">
            <div className="relative w-full max-w-lg">
              {/* Main Illustration Container */}
              <div className="relative bg-white rounded-2xl shadow-xl p-6 border">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-gray-700">
                      {t("verifyCode.securityCode")}
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                </div>

                {/* Code Editor Mockup */}
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">1</span>
                    <span className="text-purple-400">function</span>
                    <span className="text-blue-400">verifyCode</span>
                    <span className="text-yellow-400">(</span>
                    <span className="text-white">code</span>
                    <span className="text-yellow-400">)</span>
                    <span className="text-white">{"{"}</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-500">2</span>
                    <span className="text-red-400">if</span>
                    <span className="text-yellow-400">(</span>
                    <span className="text-white">code.length</span>
                    <span className="text-white"> !== </span>
                    <span className="text-cyan-400">6</span>
                    <span className="text-yellow-400">)</span>
                    <span className="text-white">{"{"}</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-8">
                    <span className="text-gray-500">3</span>
                    <span className="text-purple-400">throw</span>
                    <span className="text-white">new</span>
                    <span className="text-pink-400">Error</span>
                    <span className="text-white">(</span>
                    <span className="text-green-400">
                      'Invalid code length'
                    </span>
                    <span className="text-white">)</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-500">4</span>
                    <span className="text-white">{"}"}</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-500">5</span>
                    <span className="text-blue-400">submitCode</span>
                    <span className="text-white">(</span>
                    <span className="text-white">code</span>
                    <span className="text-white">)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">6</span>
                    <span className="text-white">{"}"}</span>
                  </div>
                </div>

                {/* Security Tips */}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {t("verifyCode.securityTips")}
                  </h3>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <span>{t("verifyCode.dontShareCode")}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <span>{t("verifyCode.checkSpam")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-3 -right-3 bg-primary-400 rounded-full p-2 shadow-lg animate-bounce">
                <Shield className="h-5 w-5 text-primary-900" />
              </div>
              <div className="absolute -bottom-3 -left-3 bg-primary rounded-full p-2 shadow-lg animate-pulse">
                <Code className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default VerifyCode;
